import {
  approveDesktopPair,
  fetchAuthMe,
  loginWithPassword,
  pollDesktopPair,
  registerWithPassword,
  requestPasswordReset,
  resetPasswordWithToken,
  startDesktopPair,
  verifyEmailWithToken,
} from "@shared/ui/sessionAuth";
import { notifyAuthChanged } from "./authUi";

const MODAL_ID = "sfe-web-login-modal";
const BODY_CLASS = "sfe-login-required";

type AuthMode = "login" | "register" | "forgot" | "reset" | "verify";

let authMode: AuthMode = "login";
let pendingResetToken = "";
let pendingVerifyToken = "";
/** Self-serve signup; refreshed from /auth/me. Default closed so stale invite links stay dead. */
let registrationOpen = false;
/** Once the user focuses password, stop stripping browser autofill. */
let passwordFieldTouched = false;
let orphanClearTimer: number | null = null;
let pairPollTimer: number | null = null;
let pairOfferStarted = false;
const DESKTOP_PAIR_KEY = "sfe-desktop-pair";

function brandHtml(): string {
  return `<span class="sfe-brand-wordmark">StockForge<b>+</b></span>`;
}

function renderLoginMarkup(mode: AuthMode = authMode): string {
  if (mode === "forgot") {
    return `
    <div class="sfe-modal sfe-column-settings-modal sfe-web-settings-modal sfe-web-login-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-web-login-title">
      <div class="sfe-modal-header">
        <h3 id="sfe-web-login-title">Forgot password</h3>
      </div>
      <form id="sfe-web-auth-form" class="sfe-web-login-form" action="/auth/forgot-password" method="post" autocomplete="on">
      <div class="sfe-web-settings-modal-body">
        <section class="sfe-web-settings-card">
          <p class="sfe-column-settings-lead sfe-login-lead">Enter your email and we&rsquo;ll send a ${brandHtml()} reset link.</p>
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>Email</span>
            <input type="email" name="username" autocomplete="username" spellcheck="false" />
          </label>
          <p class="muted small sfe-web-settings-status" data-login-status hidden></p>
        </section>
      </div>
      <div class="sfe-modal-actions sfe-column-settings-actions sfe-login-actions">
        <button type="button" class="small sfe-login-switch-btn" data-action="switch-login">Back to sign in</button>
        <button type="submit" class="small">Send reset link</button>
      </div>
      </form>
    </div>`;
  }

  if (mode === "reset") {
    return `
    <div class="sfe-modal sfe-column-settings-modal sfe-web-settings-modal sfe-web-login-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-web-login-title">
      <div class="sfe-modal-header">
        <h3 id="sfe-web-login-title">Set new password</h3>
      </div>
      <form id="sfe-web-auth-form" class="sfe-web-login-form" action="/auth/reset-password" method="post" autocomplete="on">
      <div class="sfe-web-settings-modal-body">
        <section class="sfe-web-settings-card">
          <p class="sfe-column-settings-lead sfe-login-lead">Choose a new password for your ${brandHtml()} account (at least 10 characters).</p>
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>New password</span>
            <input type="password" name="new-password" autocomplete="new-password" />
          </label>
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>Confirm password</span>
            <input type="password" name="new-password-confirm" autocomplete="new-password" />
          </label>
          <p class="muted small sfe-web-settings-status" data-login-status hidden></p>
        </section>
      </div>
      <div class="sfe-modal-actions sfe-column-settings-actions sfe-login-actions">
        <button type="button" class="small sfe-login-switch-btn" data-action="switch-login">Back to sign in</button>
        <button type="submit" class="small">Update password</button>
      </div>
      </form>
    </div>`;
  }

  if (mode === "verify") {
    return `
    <div class="sfe-modal sfe-column-settings-modal sfe-web-settings-modal sfe-web-login-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-web-login-title">
      <div class="sfe-modal-header">
        <h3 id="sfe-web-login-title">Verify email</h3>
      </div>
      <div class="sfe-web-settings-modal-body">
        <section class="sfe-web-settings-card">
          <p class="sfe-column-settings-lead sfe-login-lead">Confirming your ${brandHtml()} email address&hellip;</p>
          <p class="muted small sfe-web-settings-status" data-login-status>Verifying…</p>
        </section>
      </div>
      <div class="sfe-modal-actions sfe-column-settings-actions sfe-login-actions">
        <button type="button" class="small sfe-login-switch-btn" data-action="switch-login">Back to sign in</button>
      </div>
    </div>`;
  }

  const isRegister = mode === "register" && registrationOpen;
  const title = isRegister ? "Create account" : "Sign in";
  const lead = isRegister
    ? `Create a ${brandHtml()} account with your email and password.`
    : `Sign in to ${brandHtml()} with your email and password.`;
  const submitLabel = isRegister ? "Create account" : "Sign in";
  const switchBtn = isRegister
    ? `<button type="button" class="small sfe-login-switch-btn" data-action="switch-login">Already have an account? Sign in</button>`
    : registrationOpen
      ? `<button type="button" class="small sfe-login-switch-btn" data-action="switch-register">Create account</button>`
      : "";
  const confirmField = isRegister
    ? `
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>Confirm password</span>
            <input type="password" name="new-password-confirm" autocomplete="new-password" />
          </label>`
    : "";
  const forgotLink = isRegister
    ? ""
    : `<button type="button" class="small sfe-login-forgot-btn" data-action="switch-forgot">Forgot password?</button>`;
  const passwordSignIn =
    !isRegister && isElectronTerminal()
      ? `<button type="button" class="small sfe-login-passwords-btn" data-action="desktop-passwords">Sign in with Passwords</button>
          <p class="muted small sfe-login-passwords-note">The terminal itself cannot use macOS Passwords. This opens the browser, where the password can fill, then signs this window in.</p>`
      : "";
  const formAction = isRegister ? "/auth/register" : "/auth/login";
  return `
    <div class="sfe-modal sfe-column-settings-modal sfe-web-settings-modal sfe-web-login-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-web-login-title">
      <div class="sfe-modal-header">
        <h3 id="sfe-web-login-title">${title}</h3>
      </div>
      <form id="sfe-web-auth-form" class="sfe-web-login-form" action="${formAction}" method="post" autocomplete="on">
      <div class="sfe-web-settings-modal-body">
        <section class="sfe-web-settings-card">
          <p class="sfe-column-settings-lead sfe-login-lead">${lead}</p>
          ${passwordSignIn}
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>Email</span>
            <input type="email" name="username" autocomplete="username" autocapitalize="none" spellcheck="false" />
          </label>
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>Password</span>
            <input type="password" name="password" autocomplete="${isRegister ? "new-password" : "current-password"}" />
          </label>
          ${confirmField}
          ${forgotLink}
          <p class="muted small sfe-web-settings-status" data-login-status hidden></p>
        </section>
      </div>
      <div class="sfe-modal-actions sfe-column-settings-actions sfe-login-actions">
        ${switchBtn}
        <button type="submit" class="small">${submitLabel}</button>
      </div>
      </form>
    </div>`;
}

function setStatus(overlay: HTMLElement, message: string): void {
  const status = overlay.querySelector<HTMLElement>("[data-login-status]");
  if (!status) return;
  status.hidden = !message;
  status.textContent = message;
}

function setLoginRequiredUi(required: boolean): void {
  document.body.classList.toggle(BODY_CLASS, required);
}

function clearPasswordFields(overlay: HTMLElement): void {
  overlay
    .querySelectorAll<HTMLInputElement>(
      'input[name="password"], input[name="new-password"], input[name="new-password-confirm"]',
    )
    .forEach((input) => {
      input.value = "";
    });
}

/**
 * Chrome often autofills password alone (email stays empty). Strip that orphan
 * fill so the field starts empty, but keep autocomplete attrs so password
 * managers still work when the user focuses the field or fills email+password.
 */
function isBrowserAutofill(input: HTMLInputElement): boolean {
  try {
    return input.matches(":-webkit-autofill");
  } catch {
    return false;
  }
}

function isElectronTerminal(): boolean {
  return /\bElectron\b/.test(navigator.userAgent);
}

function clearOrphanPasswordAutofill(overlay: HTMLElement): void {
  if (passwordFieldTouched || authMode !== "login") return;
  const email = overlay.querySelector<HTMLInputElement>('input[name="username"]');
  const password = overlay.querySelector<HTMLInputElement>('input[name="password"]');
  if (!email || !password) return;
  if (isBrowserAutofill(password)) return;
  if (!email.value.trim() && password.value) {
    password.value = "";
  }
}

function stopOrphanPasswordWatch(): void {
  if (orphanClearTimer != null) {
    window.clearInterval(orphanClearTimer);
    orphanClearTimer = null;
  }
}

function startOrphanPasswordWatch(overlay: HTMLElement): void {
  stopOrphanPasswordWatch();
  passwordFieldTouched = false;
  clearOrphanPasswordAutofill(overlay);
  let ticks = 0;
  orphanClearTimer = window.setInterval(() => {
    clearOrphanPasswordAutofill(overlay);
    ticks += 1;
    if (ticks >= 20 || passwordFieldTouched) stopOrphanPasswordWatch();
  }, 100);
}

function bindPasswordField(overlay: HTMLElement): void {
  const password = overlay.querySelector<HTMLInputElement>(
    'input[name="password"], input[name="new-password"]',
  );
  const confirm = overlay.querySelector<HTMLInputElement>('input[name="new-password-confirm"]');
  const email = overlay.querySelector<HTMLInputElement>('input[name="username"]');
  const markTouched = (): void => {
    passwordFieldTouched = true;
    stopOrphanPasswordWatch();
  };
  password?.addEventListener("focus", markTouched);
  password?.addEventListener("pointerdown", markTouched);
  confirm?.addEventListener("focus", markTouched);
  email?.addEventListener("input", () => {
    if (email.value.trim()) stopOrphanPasswordWatch();
  });
}

function clearAuthQueryParams(): void {
  const url = new URL(window.location.href);
  const hadQuery = url.searchParams.has("auth") || url.searchParams.has("token");
  const hadHashToken = /(?:^|[&#])token=/i.test(url.hash);
  if (!hadQuery && !hadHashToken) return;
  url.searchParams.delete("auth");
  url.searchParams.delete("token");
  url.hash = "";
  const next = `${url.pathname}${url.search}`;
  window.history.replaceState({}, "", next);
}

function stopPairPoll(): void {
  if (pairPollTimer != null) {
    window.clearInterval(pairPollTimer);
    pairPollTimer = null;
  }
}

function showDesktopPairDone(): void {
  let note = document.getElementById("sfe-desktop-pair-note");
  if (!note) {
    note = document.createElement("p");
    note.id = "sfe-desktop-pair-note";
    note.className = "sfe-desktop-pair-note";
    document.body.appendChild(note);
  }
  note.textContent = "Signed in. Return to the StockForge+ terminal.";
}

async function approveStoredDesktopPair(): Promise<void> {
  const code = sessionStorage.getItem(DESKTOP_PAIR_KEY)?.trim() ?? "";
  if (!code) return;
  await approveDesktopPair(code);
  sessionStorage.removeItem(DESKTOP_PAIR_KEY);
  showDesktopPairDone();
}

async function startDesktopPasswordSignIn(overlay: HTMLElement): Promise<void> {
  stopPairPoll();
  setStatus(overlay, "Opening the browser so Passwords can fill your email and password…");
  let started: Awaited<ReturnType<typeof startDesktopPair>>;
  try {
    started = await startDesktopPair();
  } catch (error) {
    setStatus(overlay, error instanceof Error ? error.message : "Could not start sign-in");
    return;
  }
  const url = new URL("/web/", window.location.origin);
  url.searchParams.set("desktop-pair", started.code);
  const opened = await window.sfeDesktop?.openExternal?.(url.href);
  if (!opened) {
    setStatus(overlay, "Could not open the browser. Type the password here instead.");
    return;
  }
  const deadline = Date.now() + started.expiresInMs;
  pairPollTimer = window.setInterval(() => {
    void (async () => {
      if (Date.now() > deadline) {
        stopPairPoll();
        setStatus(overlay, "Sign-in window expired. Use Sign in with Passwords again.");
        return;
      }
      try {
        const result = await pollDesktopPair(started.code, started.pollSecret);
        if (result.pending) return;
        stopPairPoll();
        setStatus(overlay, `Signed in as ${result.user.email}`);
        closeWebLoginModal();
        notifyAuthChanged();
      } catch (error) {
        stopPairPoll();
        setStatus(overlay, error instanceof Error ? error.message : "Sign-in failed");
      }
    })();
  }, 1000);
}

function paintMode(overlay: HTMLElement, mode: AuthMode): void {
  authMode = mode;
  stopOrphanPasswordWatch();
  overlay.innerHTML = renderLoginMarkup(mode);
  bindPasswordField(overlay);
  if (mode === "login" && !isElectronTerminal()) startOrphanPasswordWatch(overlay);
  if (mode === "login" && isElectronTerminal() && !pairOfferStarted) {
    pairOfferStarted = true;
    void startDesktopPasswordSignIn(overlay);
  }
  if (mode === "verify") {
    void runVerify(overlay);
    return;
  }
  const focusName = mode === "reset" ? "new-password" : "username";
  overlay.querySelector<HTMLInputElement>(`input[name="${focusName}"]`)?.focus();
}

function ensureLoginModal(): HTMLElement {
  let overlay = document.getElementById(MODAL_ID);
  if (overlay) return overlay;

  overlay = document.createElement("div");
  overlay.id = MODAL_ID;
  overlay.className = "sfe-modal-overlay sfe-login-modal-overlay";
  overlay.innerHTML = renderLoginMarkup("login");
  document.body.appendChild(overlay);
  bindPasswordField(overlay);

  overlay.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target === overlay) return;
    const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
    if (action === "switch-register") {
      if (!registrationOpen) {
        paintMode(overlay, "login");
        setStatus(overlay, "Account creation is closed");
        return;
      }
      paintMode(overlay, "register");
      return;
    }
    if (action === "switch-login") {
      pendingResetToken = "";
      pendingVerifyToken = "";
      paintMode(overlay, "login");
      return;
    }
    if (action === "switch-forgot") {
      paintMode(overlay, "forgot");
      return;
    }
    if (action === "desktop-passwords") {
      pairOfferStarted = true;
      void startDesktopPasswordSignIn(overlay);
      return;
    }
  });

  overlay.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== "sfe-web-auth-form") return;
    event.preventDefault();
    void submitAuth(overlay);
  });

  overlay.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
  });

  return overlay;
}

async function runVerify(overlay: HTMLElement): Promise<void> {
  const token = pendingVerifyToken;
  if (!token) {
    setStatus(overlay, "Missing verification token.");
    return;
  }
  setStatus(overlay, "Verifying…");
  try {
    const result = await verifyEmailWithToken(token);
    clearAuthQueryParams();
    pendingVerifyToken = "";
    setStatus(overlay, result.message);
    notifyAuthChanged();
  } catch (error) {
    setStatus(overlay, error instanceof Error ? error.message : "Verification failed");
  }
}

async function submitAuth(overlay: HTMLElement): Promise<void> {
  const email = overlay.querySelector<HTMLInputElement>('input[name="username"]')?.value ?? "";
  const password =
    overlay.querySelector<HTMLInputElement>('input[name="password"], input[name="new-password"]')
      ?.value ?? "";
  const confirm =
    overlay.querySelector<HTMLInputElement>('input[name="new-password-confirm"]')?.value ?? "";

  if (authMode === "forgot") {
    setStatus(overlay, "Sending reset link…");
    try {
      const result = await requestPasswordReset(email);
      // resetUrl is only returned when AUTH_EMAIL_DEV_LINKS=true (local testing).
      const extra = result.resetUrl ? ` Dev link: ${result.resetUrl}` : "";
      setStatus(overlay, `${result.message}${extra}`);
    } catch (error) {
      setStatus(overlay, error instanceof Error ? error.message : "Request failed");
    }
    return;
  }

  if (authMode === "reset") {
    if (password !== confirm) {
      setStatus(overlay, "Passwords do not match.");
      clearPasswordFields(overlay);
      overlay.querySelector<HTMLInputElement>('input[name="new-password"]')?.focus();
      return;
    }
    setStatus(overlay, "Updating password…");
    try {
      const result = await resetPasswordWithToken(pendingResetToken, password);
      clearAuthQueryParams();
      pendingResetToken = "";
      setStatus(overlay, result.message);
      paintMode(overlay, "login");
      setStatus(overlay, result.message);
    } catch (error) {
      setStatus(overlay, error instanceof Error ? error.message : "Reset failed");
      clearPasswordFields(overlay);
    }
    return;
  }

  if (authMode === "register") {
    if (password !== confirm) {
      setStatus(overlay, "Passwords do not match.");
      clearPasswordFields(overlay);
      overlay.querySelector<HTMLInputElement>('input[name="new-password"]')?.focus();
      return;
    }
    setStatus(overlay, "Creating account…");
    try {
      const result = await registerWithPassword(email, password);
      const verifyNote = result.verificationEmailSent
        ? " Check your email to verify your address."
        : " We could not send a verification email yet — use Resend verification in Settings.";
      setStatus(overlay, `Account created for ${result.user.email}.${verifyNote}`);
      closeWebLoginModal();
      notifyAuthChanged();
    } catch (error) {
      setStatus(overlay, error instanceof Error ? error.message : "Create account failed");
      clearPasswordFields(overlay);
    }
    return;
  }

  setStatus(overlay, "Signing in…");
  try {
    const result = await loginWithPassword(email, password);
    await approveStoredDesktopPair().catch(() => {});
    setStatus(overlay, `Signed in as ${result.user.email}`);
    closeWebLoginModal();
    notifyAuthChanged();
  } catch (error) {
    setStatus(overlay, error instanceof Error ? error.message : "Sign in failed");
    clearPasswordFields(overlay);
  }
}

function readAuthDeepLink(): { mode: AuthMode; token: string } | null {
  const params = new URLSearchParams(window.location.search);
  // Prefer fragment token (not sent to servers/logs/Referer). Fall back to
  // query token for older email links still in flight.
  const hashParams = new URLSearchParams(
    window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash,
  );
  const auth = params.get("auth") || hashParams.get("auth") || "";
  const token =
    (hashParams.get("token") || params.get("token") || "").trim();
  if (auth === "reset" && token) return { mode: "reset", token };
  if (auth === "verify" && token) return { mode: "verify", token };
  return null;
}

async function refreshRegistrationOpen(): Promise<void> {
  try {
    const me = await fetchAuthMe();
    registrationOpen = me.registrationOpen === true;
  } catch {
    registrationOpen = false;
  }
}

export function openWebLoginModal(opts?: { mode?: AuthMode; token?: string }): void {
  const overlay = ensureLoginModal();
  const deep = readAuthDeepLink();
  let mode = opts?.mode ?? deep?.mode ?? "login";
  const token = opts?.token ?? deep?.token ?? "";
  if (mode === "reset") pendingResetToken = token;
  if (mode === "verify") pendingVerifyToken = token;
  // Drop secrets from the address bar as soon as they are in memory (H5).
  if (deep || opts?.token) clearAuthQueryParams();
  if (mode === "register" && !registrationOpen) mode = "login";
  paintMode(overlay, mode);
  overlay.classList.add("sfe-modal-open");
  setLoginRequiredUi(true);
  void refreshRegistrationOpen().then(() => {
    if (!overlay.classList.contains("sfe-modal-open")) return;
    if (authMode === "register" && !registrationOpen) {
      paintMode(overlay, "login");
      setStatus(overlay, "Account creation is closed");
      return;
    }
    if (authMode === "login" || authMode === "register") {
      paintMode(overlay, authMode);
    }
  });
}

export function closeWebLoginModal(): void {
  stopOrphanPasswordWatch();
  stopPairPoll();
  pairOfferStarted = false;
  document.getElementById(MODAL_ID)?.classList.remove("sfe-modal-open");
  setLoginRequiredUi(false);
  authMode = "login";
  passwordFieldTouched = false;
}

/** Browser tab opened by the terminal: sign in with Passwords, then hand the session back. */
export async function consumeDesktopPairLink(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("desktop-pair")?.trim() ?? "";
  if (!/^[A-Za-z0-9_-]{32,80}$/.test(code)) return false;
  params.delete("desktop-pair");
  const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", next);
  sessionStorage.setItem(DESKTOP_PAIR_KEY, code);
  const me = await fetchAuthMe().catch(() => null);
  if (me?.authenticated) {
    try {
      await approveStoredDesktopPair();
    } catch (error) {
      showDesktopPairDone();
      const note = document.getElementById("sfe-desktop-pair-note");
      if (note) {
        note.textContent = error instanceof Error ? error.message : "Could not finish sign-in.";
      }
    }
    return true;
  }
  openWebLoginModal();
  return true;
}

export function isWebLoginModalOpen(): boolean {
  return Boolean(document.getElementById(MODAL_ID)?.classList.contains("sfe-modal-open"));
}

/** Handle ?auth=reset|verify with #token=… on boot (opens modal even if already signed in for verify). */
export function consumeAuthDeepLink(): boolean {
  const deep = readAuthDeepLink();
  if (!deep) return false;
  openWebLoginModal({ mode: deep.mode, token: deep.token });
  return true;
}
